import React, { useContext, useMemo, useRef, useState } from "react";
import { AppContext } from "../../Context/Provider";
import { useEffect } from "react";
import {
  getPasswordRuleStates,
  validatePassword,
} from "../../Utilities/validatePassword";

const PENDING_AUTH_KEY = "rikaabPendingAuth";

const readPendingAuth = () => {
  try {
    const pendingAuth = JSON.parse(sessionStorage.getItem(PENDING_AUTH_KEY));
    const isSupportedMode = ["otp", "resetOtp"].includes(pendingAuth?.mode);
    const hasRequiredTarget =
      (pendingAuth?.mode === "otp" &&
        pendingAuth?.userId &&
        pendingAuth?.loginChallenge) ||
      (pendingAuth?.mode === "resetOtp" && pendingAuth?.requestId);
    const isRecent = Date.now() - Number(pendingAuth?.savedAt || 0) < 30 * 60 * 1000;

    if (isSupportedMode && hasRequiredTarget && isRecent) {
      return pendingAuth;
    }
  } catch (error) {
    // Invalid browser state is discarded below.
  }

  sessionStorage.removeItem(PENDING_AUTH_KEY);
  return {};
};

const clearPendingAuth = () => sessionStorage.removeItem(PENDING_AUTH_KEY);

function LoginPage() {
  const {
    LoginUser,
    VerifyOTP,
    ResendLoginOTP,
    ForgotPassword,
    ResendPasswordResetOTP,
    VerifyPasswordResetOTP,
    ResetPassword,
  } = useContext(AppContext);
  const initialPendingAuth = useMemo(readPendingAuth, []);
  const [mode, setMode] = useState(initialPendingAuth.mode || "login");
  const [username, setUsername] = useState(initialPendingAuth.username || "");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [pendingUserId, setPendingUserId] = useState(
    initialPendingAuth.userId || "",
  );
  const [loginChallenge, setLoginChallenge] = useState(
    initialPendingAuth.loginChallenge || "",
  );
  const [pendingEmail, setPendingEmail] = useState(
    initialPendingAuth.email || "",
  );
  const [passwordResetRequestId, setPasswordResetRequestId] = useState(
    initialPendingAuth.requestId || "",
  );
  const [passwordResetToken, setPasswordResetToken] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(() =>
    Math.max(
      0,
      Math.ceil((Number(initialPendingAuth.resendAvailableAt || 0) - Date.now()) / 1000),
    ),
  );
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const autoSubmittedOtpRef = useRef("");

  const usernameReference = useMemo(() => {
    if (mode === "login" || mode === "otp") {
      return username.trim();
    }

    return "";
  }, [mode, username]);

  const passwordRules = getPasswordRuleStates(newPassword, usernameReference);

  useEffect(() => {
    if (resendCountdown <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setResendCountdown((currentValue) => Math.max(0, currentValue - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendCountdown]);

  const startResendCountdown = (seconds = 60) => {
    const countdown = Math.max(0, Math.ceil(Number(seconds) || 60));
    setResendCountdown(countdown);
    return Date.now() + countdown * 1000;
  };

  const savePendingAuth = (data) => {
    sessionStorage.setItem(
      PENDING_AUTH_KEY,
      JSON.stringify({ ...data, savedAt: Date.now() }),
    );
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError("");
    setOtp("");
    setResendCountdown(0);

    if (nextMode === "login") {
      clearPendingAuth();
      setIdentifier("");
      setNewPassword("");
      setConfirmPassword("");
      setPendingUserId("");
      setLoginChallenge("");
      setPendingEmail("");
      setPasswordResetRequestId("");
      setPasswordResetToken("");
    }
  };

  const handleLogin = async () => {
    if (!username.trim() || !password) {
      setError("Please enter your username and password.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const result = await LoginUser({
        username: username.trim(),
        password,
      });

      if (result?.otpRequired) {
        setPendingUserId(result.userId || "");
        setLoginChallenge(result.loginChallenge || "");
        setPendingEmail(result.email || "");
        setMode("otp");
        setOtp("");
        const resendAvailableAt = startResendCountdown(
          result.retryAfterSeconds,
        );
        savePendingAuth({
          mode: "otp",
          username: username.trim(),
          userId: result.userId || "",
          loginChallenge: result.loginChallenge || "",
          email: result.email || "",
          resendAvailableAt,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOTP = async (otpValue = otp) => {
    if (
      !pendingUserId ||
      !loginChallenge ||
      otpValue.length !== 6 ||
      isSubmitting
    ) {
      setError("Please enter the 6-digit OTP.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const result = await VerifyOTP({
        userId: pendingUserId,
        loginChallenge,
        OTP: otpValue,
        username: username.trim(),
      });
      if (result?.success) {
        clearPendingAuth();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!identifier.trim()) {
      setError("Please enter your username or email.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const result = await ForgotPassword({
        identifier: identifier.trim(),
      });

      if (result?.success) {
        setPasswordResetRequestId(result.requestId || "");
        setPasswordResetToken("");
        setPendingEmail("");
        setMode("resetOtp");
        setOtp("");
        setNewPassword("");
        setConfirmPassword("");
        const resendAvailableAt = startResendCountdown(
          result.retryAfterSeconds,
        );
        savePendingAuth({
          mode: "resetOtp",
          requestId: result.requestId || "",
          resendAvailableAt,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyPasswordResetOTP = async (otpValue = otp) => {
    if (!passwordResetRequestId || otpValue.length !== 6 || isSubmitting) {
      setError("Please enter the 6-digit reset code.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const result = await VerifyPasswordResetOTP({
        requestId: passwordResetRequestId,
        OTP: otpValue,
      });

      if (result?.success) {
        clearPendingAuth();
        setPasswordResetToken(result.resetToken || "");
        setMode("reset");
        setOtp("");
        setResendCountdown(0);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpChange = (event) => {
    const nextOtp = event.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(nextOtp);
    setError("");

    if (nextOtp.length < 6) {
      autoSubmittedOtpRef.current = "";
      return;
    }

    const submissionKey = `${mode}:${nextOtp}`;
    if (isSubmitting || autoSubmittedOtpRef.current === submissionKey) {
      return;
    }

    autoSubmittedOtpRef.current = submissionKey;

    if (mode === "otp") {
      handleVerifyOTP(nextOtp);
    } else if (mode === "resetOtp") {
      handleVerifyPasswordResetOTP(nextOtp);
    }
  };

  const handleResetPassword = async () => {
    if (!passwordResetToken) {
      setError("Please verify your reset code again.");
      return;
    }

    const passwordError = validatePassword(newPassword, usernameReference);

    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      const result = await ResetPassword({
        resetToken: passwordResetToken,
        password: newPassword,
        confirmPassword,
      });

      if (result?.success) {
        clearPendingAuth();
        setMode("login");
        setPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setOtp("");
        setPasswordResetRequestId("");
        setPasswordResetToken("");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    const hasResendTarget =
      mode === "otp" ? pendingUserId : passwordResetRequestId;

    if (!hasResendTarget || resendCountdown > 0 || isResending) {
      return;
    }

    setError("");
    setIsResending(true);

    try {
      const resendAction =
        mode === "otp" ? ResendLoginOTP : ResendPasswordResetOTP;
      const resendPayload =
        mode === "otp"
          ? { userId: pendingUserId, loginChallenge }
          : { requestId: passwordResetRequestId };
      const result = await resendAction(resendPayload);

      if (result?.success) {
        setOtp("");
      }

      if (result?.retryAfterSeconds) {
        const resendAvailableAt = startResendCountdown(
          result.retryAfterSeconds,
        );
        savePendingAuth({
          mode,
          username: mode === "otp" ? username.trim() : "",
          userId: mode === "otp" ? pendingUserId : "",
          loginChallenge: mode === "otp" ? loginChallenge : "",
          email: mode === "otp" ? pendingEmail : "",
          requestId: mode === "resetOtp" ? passwordResetRequestId : "",
          resendAvailableAt,
        });
      }
    } finally {
      setIsResending(false);
    }
  };

  const cardTitle =
    mode === "otp"
      ? "Enter verification code"
      : mode === "forgot"
        ? "Forgot password"
        : mode === "resetOtp"
          ? "Verify reset code"
        : mode === "reset"
          ? "Reset password"
          : "Login to your account";

  return (
    <section className="bg-gray-50 dark:bg-gray-900">
      <div className="flex min-h-screen flex-col items-center justify-center px-6 py-8">
        <div className="w-full rounded-lg bg-white shadow dark:border dark:bg-gray-800 dark:border-gray-700 sm:max-w-md">
          <div className="space-y-4 p-6 sm:p-8">
            <h1 className="text-xl font-bold leading-tight tracking-tight text-gray-900 dark:text-white md:text-2xl">
              {cardTitle}
            </h1>

            {pendingEmail && mode === "otp" ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                We sent the 6-digit code to {pendingEmail}
              </p>
            ) : null}

            {mode === "resetOtp" ? (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                If an account exists, enter the reset code sent to its email.
              </p>
            ) : null}

            {error ? (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            ) : null}

            <form
              className="space-y-4"
              onSubmit={(event) => event.preventDefault()}
            >
              {mode === "login" ? (
                <>
                  <div>
                    <label
                      htmlFor="username"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Username
                    </label>
                    <input
                      id="username"
                      type="text"
                      className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-gray-900 focus:border-primary-600 focus:ring-primary-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter your username"
                      value={username}
                      onChange={(event) => setUsername(event.target.value)}
                    />
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Password
                    </label>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-10 text-gray-900 focus:border-primary-600 focus:ring-primary-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-2 top-9 inline-flex h-8 w-8 items-center justify-center rounded text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-5-10-5a18.667 18.667 0 013.877-4.697M6.11 6.11A9.953 9.953 0 0112 5c5.523 0 10 5 10 5s-1.33 2.06-3.478 3.72M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 3l18 18"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </>
              ) : null}

              {mode === "otp" || mode === "resetOtp" ? (
                <div>
                  <label
                    htmlFor="otp"
                    className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                  >
                    {mode === "otp" ? "6-digit OTP" : "6-digit reset code"}
                  </label>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-gray-900 focus:border-primary-600 focus:ring-primary-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Enter 6-digit code"
                    value={otp}
                    onChange={handleOtpChange}
                  />
                </div>
              ) : null}

              {mode === "forgot" ? (
                <div>
                  <label
                    htmlFor="identifier"
                    className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                  >
                    Username or email
                  </label>
                  <input
                    id="identifier"
                    type="text"
                    className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-gray-900 focus:border-primary-600 focus:ring-primary-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Enter your username or email"
                    value={identifier}
                    onChange={(event) => setIdentifier(event.target.value)}
                  />
                </div>
              ) : null}

              {mode === "reset" ? (
                <>
                  <div className="relative">
                    <label
                      htmlFor="new-password"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      New password
                    </label>
                    <input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-10 text-gray-900 focus:border-primary-600 focus:ring-primary-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((s) => !s)}
                      className="absolute right-2 top-9 inline-flex h-8 w-8 items-center justify-center rounded text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
                      aria-label={
                        showNewPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showNewPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-5-10-5a18.667 18.667 0 013.877-4.697M6.11 6.11A9.953 9.953 0 0112 5c5.523 0 10 5 10 5s-1.33 2.06-3.478 3.72M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 3l18 18"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="relative">
                    <label
                      htmlFor="confirm-password"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      Confirm password
                    </label>
                    <input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-10 text-gray-900 focus:border-primary-600 focus:ring-primary-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(event) =>
                        setConfirmPassword(event.target.value)
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((s) => !s)}
                      className="absolute right-2 top-9 inline-flex h-8 w-8 items-center justify-center rounded text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600"
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showConfirmPassword ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-5-10-5a18.667 18.667 0 013.877-4.697M6.11 6.11A9.953 9.953 0 0112 5c5.523 0 10 5 10 5s-1.33 2.06-3.478 3.72M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 3l18 18"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="rounded bg-gray-50 p-4 text-sm text-gray-700">
                    <p className="mb-2 font-semibold">Password policy</p>
                    <ul className="list-disc space-y-1 pl-5">
                      {passwordRules.map((rule) => (
                        <li
                          key={rule.key}
                          className={
                            rule.passed ? "text-green-600" : "text-red-600"
                          }
                        >
                          {rule.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : null}

              {mode === "otp" || mode === "resetOtp" ? (
                <div className="text-center text-sm text-gray-600 dark:text-gray-300">
                  {resendCountdown > 0 ? (
                    <span aria-live="polite">
                      Resend code in {resendCountdown} seconds
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={isResending}
                      onClick={handleResendOTP}
                      className="font-medium text-custom-green disabled:opacity-60"
                    >
                      {isResending ? "Sending..." : "Resend code"}
                    </button>
                  )}
                </div>
              ) : null}

              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  if (mode === "login") {
                    handleLogin();
                    return;
                  }

                  if (mode === "otp") {
                    handleVerifyOTP();
                    return;
                  }

                  if (mode === "forgot") {
                    handleForgotPassword();
                    return;
                  }

                  if (mode === "resetOtp") {
                    handleVerifyPasswordResetOTP();
                    return;
                  }

                  handleResetPassword();
                }}
                className="w-full rounded-lg bg-custom-green px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-custom-green0 disabled:opacity-70"
              >
                {isSubmitting
                  ? "Please wait..."
                  : mode === "otp"
                    ? "Verify OTP"
                    : mode === "forgot"
                      ? "Send reset code"
                      : mode === "resetOtp"
                        ? "Verify reset code"
                      : mode === "reset"
                        ? "Reset password"
                        : "Login"}
              </button>
            </form>

            <div className="flex justify-between text-sm text-gray-600">
              {mode === "otp" ||
              mode === "forgot" ||
              mode === "resetOtp" ||
              mode === "reset" ? (
                <button
                  type="button"
                  onClick={() => switchMode("login")}
                  className="font-medium text-custom-green"
                >
                  Back to login
                </button>
              ) : (
                <span />
              )}

              {mode === "login" ? (
                <button
                  type="button"
                  onClick={() => switchMode("forgot")}
                  className="font-medium text-custom-green"
                >
                  Forgot password?
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default LoginPage;
